import { supabase } from './supabase';

/**
 * Release the loads billed by a set of driver_statements so they become billable
 * again, i.e. set `loads.included_in_statement = false`.
 *
 * WHY THIS EXISTS
 * ---------------
 * `statement_line_items.statement_id` is ON DELETE CASCADE
 * (supabase/migrations/20260409000002_add_missing_columns.sql:107). Deleting a
 * driver_statements row therefore destroys the only record of which loads that
 * statement claimed. If the loads are not released FIRST, they stay stranded at
 * included_in_statement = true forever — they vanish from the statement wizard
 * and the driver can never be paid for them.
 *
 * MUST be awaited BEFORE the driver_statements rows are deleted.
 *
 * MULTI-STATEMENT SAFETY
 * ----------------------
 * A driver with two scheduled-deduction profiles (e.g. company driver + owner)
 * gets one statement per profile, and the same load can legitimately appear on
 * both. Deleting one of those statements must NOT release a load the surviving
 * statement still bills. So a load is only released when no OTHER statement
 * (outside `statementIds`) still has a line item for it.
 *
 * Returns the number of loads actually released. Throws on a write failure so
 * the caller can abort the delete rather than silently stranding loads.
 */
export async function releaseStatementLoads(statementIds: string[]): Promise<number> {
  const ids = Array.from(new Set(statementIds.filter(Boolean)));
  if (ids.length === 0) return 0;

  // 1. Which loads do the doomed statements bill? Chunked and filtered by
  //    statement_id, which rides idx_statement_line_items_statement_id. Chunks
  //    stay small so a single response can never hit PostgREST's 1000-row cap.
  const claimed: string[] = [];
  for (let i = 0; i < ids.length; i += 50) {
    const { data, error } = await supabase
      .from('statement_line_items')
      .select('item_id')
      .eq('item_type', 'load')
      .in('statement_id', ids.slice(i, i + 50));
    if (error) throw error;
    (data || []).forEach((row: any) => {
      if (row?.item_id) claimed.push(row.item_id as string);
    });
  }

  const loadIds = Array.from(new Set(claimed));
  if (loadIds.length === 0) return 0;

  // 2. Are any of those loads still billed by a statement that is NOT being
  //    deleted? Those must keep included_in_statement = true.
  const stillClaimed = new Set<string>();
  const doomed = new Set(ids);
  for (let i = 0; i < loadIds.length; i += 100) {
    const { data, error } = await supabase
      .from('statement_line_items')
      .select('item_id, statement_id')
      .eq('item_type', 'load')
      .in('item_id', loadIds.slice(i, i + 100));
    if (error) throw error;
    (data || []).forEach((row: any) => {
      if (row?.item_id && row?.statement_id && !doomed.has(row.statement_id)) {
        stillClaimed.add(row.item_id as string);
      }
    });
  }

  const toRelease = loadIds.filter(id => !stillClaimed.has(id));
  if (toRelease.length === 0) return 0;

  // 3. Release them.
  for (let i = 0; i < toRelease.length; i += 100) {
    const { error } = await supabase
      .from('loads')
      .update({ included_in_statement: false })
      .in('id', toRelease.slice(i, i + 100));
    if (error) throw error;
  }

  return toRelease.length;
}
